import styled from '@emotion/styled';
import { VocabWordWrapper } from '@seedlang/hoc';
import { AppUI, DeckUI } from '@seedlang/state';
import { flexCenter } from '@seedlang/style_mixins';
import Image from 'components/image';
import Spinner from 'components/spinner';
import VideoPlayerWithSupplement from 'components/media/video_player_with_supplement';
import Modal from 'components/modal';
import LearnedButton from 'components/review/learned_button';
import ReviewToggle from 'components/review/review_toggle';
import SentenceCreate from 'components/sentence_create';
import VocabWordInfoList from 'components/vocab/vocab_word_info_list';
import WorksheetBreadcrumbs from 'components/worksheet/breadcrumbs';
import WorksheetWordNavigation from 'components/worksheet/worksheet_word_navigation';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import VocabWordDetails from 'components/vocab/vocab_word_details';
import withRouteParams from 'utils/with_route_params';
import SeedlangExplainer, { SEEDLANG_EXPLAINER_UI_ELEMENT } from './seedlang_explainer';

const Wrapper = styled.div`
  margin: ${props => props.margin};
  width: 100%;
  position: relative;
  font-size: 16px;
  flex: 1;
  border-radius: ${props => props.rounded ? '12px' : '0'};
  overflow: hidden;
  .word {
    margin: 0 1px 0 0;
    margin-bottom: 0;
    font-size: 16px;
    line-height: 22px;
    cursor: default;
    border: 1px solid transparent;
  }
  .word.highlight {
    background: #feffdc;
    border-radius: 5px;
  }
  .vocab-underline {
    display: block;
    margin-bottom: 10px;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 5px;
  padding: ${props => props.isMobile ? '20px 0' : '20px 10px'};
  position: relative;
`;

const CenterRow = styled.div`
  margin: ${props => props.margin};
  ${flexCenter()}
`;

const ButtonWrapper = styled.div`
  ${flexCenter()}
  gap: 10px;
  flex-direction: column;
`;

const AdminLink = styled.div`
  font-size: 11px;
  text-decoration: underline;
  background: #ECECEC;
  text-align: center;
  padding: 5px;
`;

const TabsWrapper = styled.div`
  margin-top: 5px;
`;

const VideoPlaceholder = styled.div`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

@observer
class VocabWord extends React.Component {
  @observable showTriviaQuestionModal = false;

  componentDidMount() {
    document.getElementById('top').scrollIntoView();
  }

  @computed get fontSize() {
    return AppUI.layout.isMobile ? '13px' : '16px';
  }

  @computed get lineHeight() {
    return AppUI.layout.isMobile ? '17px' : '22px';
  }

  @computed get targetText() {
    if (AppUI.site.hideDefiniteArticle) {
      return this.props.word.targetTextWithGender;
    } else {
      return this.props.word.targetTextWithDefiniteArticle;
    }
  }

  @computed get pluralTargetText() {
    if (AppUI.site.hideDefiniteArticle) {
      return this.props.word.pluralNounsStringWithoutDefiniteArticle;
    } else {
      return this.props.word.pluralNounsString;
    }
  }

  @computed get genderVariantTargetText() {
    if (AppUI.site.hideDefiniteArticle) {
      return this.props.word.genderVariant.targetTextWithGender;
    } else {
      return this.props.word.genderVariant.targetTextWithDefiniteArticle;
    }
  }
  playSentenceVideo = (sentence) => {
    document.getElementById('top').scrollIntoView();
    this.props.playSentenceVideo(sentence);
  }

  render() {
    const rowMargin = this.props.rowMargin || '5px 0';
    const videoWidth = AppUI.layout.isMobile ? 150 : 180;
    const reviewToggle = AppUI.userIsLoaded && (AppUI.siteIsSeedlang || ['DE', 'ES', 'FR'].includes(AppUI.site.languageId)) ? (
      <ReviewToggle
        interfaceSource='vocab-word'
        buttonText={AppUI.siteIsSeedlang ? 'Add to Reviews' : 'Add to Seedlang'}
        buttonHeight='30px'
        width='240px'
        buttonBackground='#FFF'
        margin='0'
        word={this.props.word}
      />
    ) : null;
    return (
      <>
        {
          this.props.params.worksheetId &&
          <WorksheetBreadcrumbs
            showAsBreadcrumbs={AppUI.layout.isMobile}
            worksheetId={this.props.params.worksheetId}
            margin='10px 0'
          />
        }
        <Wrapper
          margin={this.props.params.worksheetId ? '10px 0 0 0' : null}
          rounded={AppUI.siteIsMemrev}
        >
          {
            this.showTriviaQuestionModal &&
            <Modal
              onCloseModal={() => this.showTriviaQuestionModal = false}
            >
              <SentenceCreate
                showTriviaOptionInputs
                triviaWordId={this.props.word.id}
              />
            </Modal>
          }
          <Section
            isMobile={AppUI.layout.isMobile}
          >
            {this.props.params.worksheetId && <WorksheetWordNavigation />}
            <CenterRow>
              {
                !this.props.hasVideoClip && this.props.showSpinner &&
                <VideoPlaceholder size={videoWidth} role='status' aria-label='Loading word video'>
                  <Spinner margin='0' />
                </VideoPlaceholder>
              }
              {
                this.props.hasVideoClip &&
                <VideoPlayerWithSupplement
                  hasVideoClip
                  autoPlay={this.props.autoPlay}
                  width={videoWidth}
                  videoClip={this.props.videoClip}
                  supplementVideoClip={this.props.supplementVideoClip}
                />
              }
              {
                this.props.supplementImageUrl &&
                <Image
                  width={Math.floor(DeckUI.layout.deckWidth / 2.8)}
                  src={this.props.supplementImageUrl}
                />
              }
            </CenterRow>
            {
              this.props.word && this.props.word.id &&
              <CenterRow
                margin='10px 0 0 0'
              >
                <ButtonWrapper>
                  {AppUI.siteIsSeedlang ? reviewToggle : (
                    <SeedlangExplainer
                      autoOpenUiElement={this.props.location?.query?.from === 'vocab-list' ? SEEDLANG_EXPLAINER_UI_ELEMENT : null}
                      isPodcastEpisode={AppUI.routeStore.routeName === 'podcast.vocab.word'}
                    >
                      {reviewToggle}
                    </SeedlangExplainer>
                  )}
                  {
                    AppUI.siteIsSeedlang &&
                    <LearnedButton
                      word={this.props.word}
                    />
                  }
                </ButtonWrapper>
              </CenterRow>
            }
            {
              Boolean(this.props.word?.id) &&
              <VocabWordInfoList
                afterFlag={this.props.getWord}
                {...this.props}
                rowMargin={rowMargin}
                headerOnly
                dictionaryMeanings
              />
            }

          {
            this.props.word &&
            <TabsWrapper
              isMobile={AppUI.layout.isMobile}
            >
              <VocabWordDetails {...this.props} rowMargin={rowMargin} />
            </TabsWrapper>
          }
          </Section>
          {
            AppUI.user.admin && this.props.params.wordId &&
            <AdminLink>
              <a
                href={`${(window.location.hostname.includes('ngrok') || window.location.hostname.includes('localhost')) ? 'http://localhost:3011' : 'https://seedlang.com'}/builder/words/${this.props.params.wordId}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                Admin Link
              </a>
            </AdminLink>
          }
        </Wrapper>
      </>
    );
  }
}

export default withRouteParams(VocabWordWrapper(VocabWord));
